// src/pages/PropertiesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { getAccessibleProperties, searchProperties } from "../api/propertyApi";
import { canWriteSales } from "../utils/roleAccess";
import StartSaleDialog from "../components/sales/StartSaleDialog";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Snackbar,
  TextField,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SellIcon from "@mui/icons-material/Sell";

function PropertiesPage() {
  const { user, permissions, roleCapabilities } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [liveFilter, setLiveFilter] = useState("live"); // "all", "live", "non-live"
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saleProperty, setSaleProperty] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const userRoles = user?.claims_role || [];
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canStartSale = canWriteSales(userRoles, permissions, roleCapabilities);

  // Load all accessible properties for current live filter.
  useEffect(() => {
    const loadProperties = async () => {
      try {
        setLoading(true);
        setError("");
        // Determine live filter value for API
        let apiLiveFilter = null;
        if (liveFilter === "live") {
          apiLiveFilter = true;
        } else if (liveFilter === "non-live") {
          apiLiveFilter = false;
        }

        const data = await getAccessibleProperties(apiLiveFilter);

        const normalized = (data || []).map((p) => ({
          ...p,
          unit_count: p.unit_count ?? p.metadata?.total_units ?? 0,
        }));

        const unique = [
          ...new Map(normalized.map((p) => [p.property_id, p])).values(),
        ];

        setProperties(unique);
        setFilteredProperties(unique);
      } catch (err) {
        console.error("Failed to load properties:", err);
        setError("Unable to load your properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, [liveFilter]);

  // Backend search to keep search logic server-side.
  useEffect(() => {
    const query = searchTerm.trim();
    if (!query) {
      setSearchLoading(false);
      setFilteredProperties(properties);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setError("");
        let apiLiveFilter = null;
        if (liveFilter === "live") apiLiveFilter = true;
        if (liveFilter === "non-live") apiLiveFilter = false;

        const results = await searchProperties(query, 100, apiLiveFilter);
        if (cancelled) return;
        const unique = [...new Map((results || []).map((p) => [p.property_id, p])).values()];
        setFilteredProperties(unique);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to search properties:", err);
        setError("Unable to search properties right now.");
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchTerm, liveFilter, properties]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const handleCardClick = (propertyId) => {
    navigate(`/properties/${propertyId}/details`);
  };

  const propertiesWithLocation = useMemo(
    () =>
      filteredProperties.filter(
        (p) =>
          p.address_latitude !== null &&
          p.address_latitude !== undefined &&
          p.address_longitude !== null &&
          p.address_longitude !== undefined
      ),
    [filteredProperties]
  );

  const locationBounds = useMemo(() => {
    if (!propertiesWithLocation.length) return null;
    const lats = propertiesWithLocation.map((p) => Number(p.address_latitude));
    const lngs = propertiesWithLocation.map((p) => Number(p.address_longitude));
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    return [
      [minLat, minLng],
      [maxLat, maxLng],
    ];
  }, [propertiesWithLocation]);

  return (
    <Layout>
      <Box
        className="page-container"
        sx={{
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              My Properties
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Properties you currently manage
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              size="small"
              label="Search properties"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={liveFilter}
                label="Filter"
                onChange={(e) => setLiveFilter(e.target.value)}
              >
                <MenuItem value="all">All Properties</MenuItem>
                <MenuItem value="live">Live Only</MenuItem>
                <MenuItem value="non-live">Non-Live Only</MenuItem>
              </Select>
            </FormControl>

            {canCreate && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/properties/create")}
                sx={{ px: 1.75, py: "8px", fontSize: 12.5 }}
              >
                Add property
              </Button>
            )}
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab label="Properties" />
          <Tab label="Locations" />
        </Tabs>

        {(loading || searchLoading) && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CircularProgress size={36} />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              {searchLoading ? "Searching properties..." : "Loading properties..."}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && activeTab === 0 && (
          <>
            {filteredProperties.length === 0 ? (
              <Typography>
                No properties found
                {liveFilter !== "all"
                  ? ` (${liveFilter === "live" ? "live" : "non-live"} only)`
                  : ""}
                .
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 3,
                }}
              >
                {filteredProperties.map((p) => (
                  <Paper
                    key={p.property_id}
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "all 0.2s ease",
                      opacity: p.live === false ? 0.7 : 1,
                      "&:hover": {
                        boxShadow: 6,
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={() => handleCardClick(p.property_id)}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {p.property_name}
                        </Typography>
                        {p.estate_code ? (
                          <Typography variant="body2" color="text.secondary">
                            {p.estate_code}
                          </Typography>
                        ) : null}
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {p.address_line1}
                        {p.address_line2 ? `, ${p.address_line2}` : ""}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {p.city}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        pt: 2,
                        mt: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {p.postcode}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {p.unit_count || 0} {p.unit_count === 1 ? "unit" : "units"}
                        </Typography>
                        {canStartSale && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SellIcon />}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSaleProperty(p);
                            }}
                          >
                            Start sale
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

        {!loading && !error && activeTab === 1 && (
          <>
            {propertiesWithLocation.length === 0 ? (
              <Typography>No properties with locations yet.</Typography>
            ) : (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: 320, md: 520 },
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <MapContainer
                    bounds={locationBounds}
                    boundsOptions={{ padding: [20, 20] }}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {propertiesWithLocation.map((p) => (
                      <CircleMarker
                        key={p.property_id}
                        center={[Number(p.address_latitude), Number(p.address_longitude)]}
                        radius={7}
                        pathOptions={{ color: "#2563eb", fillColor: "#3b82f6", fillOpacity: 0.9 }}
                      >
                        <Popup>
                          <Box sx={{ minWidth: 180 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {p.property_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {[p.address_line1, p.address_line2, p.city, p.postcode]
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => handleCardClick(p.property_id)}
                              sx={{ mt: 1, p: 0 }}
                            >
                              Open Property
                            </Button>
                          </Box>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>
      <StartSaleDialog
        open={Boolean(saleProperty)}
        propertyId={saleProperty?.property_id}
        propertyName={saleProperty?.property_name}
        onClose={() => setSaleProperty(null)}
        onStarted={(sale) => {
          setSaleProperty(null);
          showToast("Sale started.");
          navigate(`/sales/${sale.id}`);
        }}
      />
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default PropertiesPage;
